import { Fragment, useMemo, useState } from 'react';

import { useGSAP } from '@gsap/react';
import { usePrevious } from '@radix-ui/react-use-previous';
import { TextPlugin } from 'gsap/dist/TextPlugin';
import { gsap } from 'gsap/dist/gsap';

import { type ExperienceBlock } from '~/contentful/graphql/_generated/graphql';
import { trackEvent } from '~/ui/components/analytics';
import { Link } from '~/ui/components/link';

import styles from './progress.module.scss';
import { ANIMATION_DURATION, createEasedTimeline } from '../utils/utils';

gsap.registerPlugin(useGSAP, TextPlugin);

interface AAPBJson {
  metadataFields: {
    key: string;
    label: string;
  }[];
  metadata: Record<string, string>[];
}

export interface ProgressProps {
  experienceBlock: ExperienceBlock;
  currentVideo: number;
  setCurrentVideo: (video: number) => void;
  progress: number;
  metadataId: string;
}

export function Progress({
  experienceBlock,
  currentVideo,
  setCurrentVideo,
  progress,
  metadataId,
}: ProgressProps) {
  const [hoveredVideo, setHoveredVideo] = useState<number>();
  const previousProgress = usePrevious(progress);

  const json: AAPBJson = useMemo(() => experienceBlock.json, [experienceBlock.json]);
  const displayedMetadataIndex = hoveredVideo ?? currentVideo;
  const displayedMetadata: Record<string, string> = useMemo(
    () => json.metadata?.[displayedMetadataIndex] ?? {},
    [displayedMetadataIndex, json.metadata],
  );

  useGSAP(() => {
    const tl = createEasedTimeline();
    const delta = progress - previousProgress;
    if (delta === 0) {
      return;
    }
    const progressLines = gsap.utils.toArray<HTMLElement>(`.${styles.progressLine}`);
    const segmentProgress = 1 / progressLines.length;
    progressLines.forEach(progressLine => {
      const index = parseInt(progressLine.dataset.index ?? '');
      // the progress at which the current segment starts and ends
      const segmentStart = index * segmentProgress;
      const segmentEnd = (index + 1) * segmentProgress;

      // gets the progress (0-1) of the whole animation clamped to the current segment
      const getEasedProgress = gsap.utils.pipe(
        // clamp to the current segment
        gsap.utils.clamp(segmentStart, segmentEnd),
        // get the progress *within the current animation*
        gsap.utils.mapRange(previousProgress, progress, 0, 1),
      );
      const start = getEasedProgress(previousProgress);
      const end = getEasedProgress(progress);

      const innerDelta = Math.abs(end - start);
      if (innerDelta === 0) {
        if (progress <= segmentStart || progress >= segmentEnd) {
          tl.set(progressLine, {
            attr: {
              value: progress <= segmentStart ? 0 : 100,
            },
          });
        }
        return;
      }

      const mapProgressToSegment = gsap.utils.pipe(
        gsap.utils.mapRange(segmentStart, segmentEnd, 0, 100),
        gsap.utils.clamp(0, 100),
      );

      tl.fromTo(
        progressLine,
        {
          attr: {
            value: mapProgressToSegment(previousProgress),
          },
        },
        {
          attr: {
            value: mapProgressToSegment(progress),
          },
          duration: ANIMATION_DURATION * innerDelta,
        },
        start * ANIMATION_DURATION,
      );
    });
    tl.run();
  }, [previousProgress, progress]);

  useGSAP(() => {
    const root = document.querySelector<HTMLElement>(`.${styles.progress}`);
    const line = document.querySelector<HTMLElement>(
      `.${styles.progressLine}[data-index="${displayedMetadataIndex}"]`,
    );
    const metadata = document.querySelector<HTMLElement>(`.${styles.metadata}`);
    if (!root || !line || !metadata) {
      return;
    }
    const mm = gsap.matchMedia();
    mm.add(
      {
        // TODO: This shouldn't be hardcoded
        isMobile: '(max-width: 767px)',
        isDesktop: '(min-width: 768px)',
        prefersReducedMotion: '(prefers-reduced-motion: reduce)',
      },
      context => {
        const { isDesktop, prefersReducedMotion } = context.conditions || {};
        const tl = createEasedTimeline().to(`.${styles.metadata} dd`, {
          opacity: 0,
          duration: ANIMATION_DURATION / 2,
        });
        json.metadataFields.forEach(field => {
          tl.set(`.${styles.metadata} dd[data-term="${field.key}"]`, {
            text: displayedMetadata[field.key],
          });
        });
        tl.to(`.${styles.metadata} dd`, {
          opacity: 1,
          duration: ANIMATION_DURATION / 2,
        });
        if (isDesktop && !prefersReducedMotion) {
          const rootRect = root.getBoundingClientRect();
          const lineRect = line.getBoundingClientRect();
          tl.to(
            `.${styles.metadata}`,
            {
              x: lineRect.left - rootRect.left,
              duration: ANIMATION_DURATION,
            },
            0,
          );
        } else {
          // reset x position when switching between breakpoints
          tl.set(`.${styles.metadata}`, {
            x: 0,
          });
        }
        tl.run();
      },
    );
  }, [displayedMetadata, displayedMetadataIndex, json.metadataFields]);

  return (
    <div className={styles.root}>
      <div className={styles.metadata} id={metadataId}>
        <dl>
          {json.metadataFields.map((field, index) => (
            <Fragment key={index}>
              <dt>{field.label}</dt>
              <dd data-term={field.key} />
            </Fragment>
          ))}
        </dl>
        <Link
          href={displayedMetadata.link}
          event={{
            name: 'aapb_interactive_episode_link',
            data: {
              href: displayedMetadata.link,
            },
          }}
          withIcon
        >
          Watch the full episode
        </Link>
      </div>
      <div className={styles.progress}>
        <div className={styles.lines}>
          {experienceBlock.referencesCollection?.items.map((_, index) => (
            <progress
              value={0}
              max={100}
              data-index={index}
              className={styles.progressLine}
              onMouseOver={() => setHoveredVideo(index)}
              onFocus={() => setHoveredVideo(index)}
              onMouseOut={() => setHoveredVideo(undefined)}
              onBlur={() => setHoveredVideo(undefined)}
              onClick={() => {
                setCurrentVideo(index);
                trackEvent('interactive_lower_nav');
              }}
              key={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
